import { useEffect, useRef } from "react";
import { useProfile, useUpdateProfile } from "@/hooks/useProfile";
import { useAuthStore } from "@/stores/useAuthStore";
import { useUIStore } from "@/stores/useUIStore";
import type { SupportedLanguage } from "@/lib/types";

/**
 * Makes the authenticated member's profile the durable language setting.
 *
 * Existing profiles start with NULL and are seeded from the member's CHOICE,
 * never from a browser guess -- see `languageIsExplicit`. A guess written here
 * would become authoritative for their outbound email (resolveOutreachLocale
 * returns the preference ahead of every inference rule), silently switching
 * the mail language of members who never asked for it. NULL keeps the existing
 * country inference, which is the honest answer when nobody has chosen.
 *
 * Once set, the profile wins on new browsers and future sessions.
 */
export function usePreferredLanguageSync(): void {
  const userId = useAuthStore((s) => s.session?.userId ?? null);
  const profileSynced = useAuthStore((s) => s.profileSynced);
  const language = useUIStore((s) => s.language);
  const languageIsExplicit = useUIStore((s) => s.languageIsExplicit);
  const setLanguage = useUIStore((s) => s.setLanguage);
  const { data: profile } = useProfile(undefined, {
    // The login bridge creates/synchronizes the profile first. Querying before
    // that race settles can cache a transient "row not found" for a new member.
    enabled: userId !== null && profileSynced,
  });
  const { mutate: updateProfile, failureCount, isPending } = useUpdateProfile();
  const hydratedUser = useRef<string | null>(null);
  const lastPersisted = useRef<SupportedLanguage | null>(null);
  const attemptedLanguage = useRef<SupportedLanguage | null>(null);
  const attempts = useRef(0);
  // The language in effect before the profile query resolved. The switcher is
  // interactive during that round-trip, so hydration has to be able to tell
  // "still the value we started with" from "the member just picked one".
  const languageAtMount = useRef(language);

  useEffect(() => {
    if (userId === null) {
      hydratedUser.current = null;
      lastPersisted.current = null;
      attemptedLanguage.current = null;
      attempts.current = 0;
      return;
    }
    if (!profile) return;

    if (hydratedUser.current !== userId) {
      hydratedUser.current = userId;
      if (profile.preferredLanguage) {
        // What the server currently holds, either way.
        lastPersisted.current = profile.preferredLanguage;
        // Only ADOPT it if the member has not picked something else in the
        // meantime. Hydrating unconditionally overwrote a fresh pick and then
        // returned -- and because lastPersisted was set to the reverted value,
        // no later pass re-persisted it, so the choice was lost for good
        // rather than merely flickering.
        if (language === languageAtMount.current) {
          attemptedLanguage.current = profile.preferredLanguage;
          if (language !== profile.preferredLanguage) setLanguage(profile.preferredLanguage);
          return;
        }
      } else {
        lastPersisted.current = null;
      }
    }

    if (lastPersisted.current === language || isPending) return;
    // Seed only from a choice. `lastPersisted === null` is the never-set
    // member; without an explicit pick there is nothing to record, and a
    // browser guess must not be promoted into one.
    if (lastPersisted.current === null && !languageIsExplicit) return;
    if (attemptedLanguage.current !== language) {
      attemptedLanguage.current = language;
      attempts.current = 0;
    }
    if (attempts.current >= 3) return;

    attempts.current += 1;
    const target = language;
    updateProfile(
      { preferredLanguage: target },
      {
        onSuccess: () => {
          lastPersisted.current = target;
          attempts.current = 0;
        },
        // Without this the write fails into nothing: no toast, no console, and
        // a NULL column that reads exactly like "never chose one", so neither
        // the member nor an operator can tell a failed save from an absent
        // preference. The cap below then stops retrying for the session.
        onError: (error) => {
          console.error("[preferredLanguage] save failed", {
            target,
            attempt: attempts.current,
            error,
          });
        },
      },
    );
  }, [failureCount, isPending, language, languageIsExplicit, profile, setLanguage, updateProfile, userId]);
}
